import _ from 'lodash';
import { history } from 'umi';

import { bindServices } from '@/services/api';
import SocketService from '@/services/socket.io';

const api = bindServices('processes');
import { formFieldBehaviorsMapping } from '@/utils/core/business-logic';
import { toPaginate } from '@/utils/infrastructure';
import { CoreEnums } from '@/utils/domain/constants';
import CoreQueries from '@/utils/input/queries';
import { filtersConfig } from './ManageProcess/utils';
import { groupsFiltersConfig } from './manage-groups/utils';
import { queryProcessConfig, startProcess, updateFavorite } from './service';

const { ManageProcess } = CoreQueries;

const meta = {
  page: 1,
  limit: 5,
  count: 0,
  totalPages: 0,
};

const initialState = {
  data: [],
  searchData: [],
  allGroups: [],
  favorites: [],
  processList: [],
  currentItem: {},
  newGroup: false,
  selectedItem: null,
  showGroupDetail: false,
  searchCriteria: null,
  pagination: meta,
  processListPagination: meta,
  activeTab: 'groups',
  favorite: false,
  filtersConfig,
  groupsFiltersConfig,
  favoritesChanged: false,
  // ... start process
  validationErrors: [],
  processConfig: null,
  redirectTo: null,
};

const Model = {
  namespace: 'processes',
  state: initialState,
  effects: {
    *fetchData({ payload }, { call, put }) {
      try {
        const params = { route: 'process/groups', ...payload, ...ManageProcess.group };
        const res = yield call(payload.id ? api.getById : api.getAll, params);
        yield put({
          type: 'queryGroups',
          payload: {
            ...res,
            ..._.pick(payload, ['page', 'limit']),
          },
        });
      } catch (e) {}
    },

    *fetchList({ payload }, { call, put }) {
      try {
        const params = { route: 'process/allowed', ...payload, ...ManageProcess.allowed };
        const res = yield call(api.getAll, params);
        yield put({
          type: 'queryList',
          payload: {
            ...res,
            ..._.pick(payload, ['page', 'limit']),
          },
        });
      } catch (e) {}
    },

    *fetchFavorites({ payload }, { call, put }) {
      try {
        const params = { route: 'process/favorites', ...payload, ...ManageProcess.favorite };
        const res = yield call(api.getAll, params);
        yield put({ type: 'queryFavorites', payload: res });
      } catch (e) {}
    },

    *fetchConfig({ payload }, { call, put }) {
      try {
        const res = yield call(queryProcessConfig, payload);
        yield put({ type: 'queryConfig', payload: res });
      } catch (e) {
        yield put({ type: 'queryConfig' });
        history.replace('/processes');
      }
    },

    *setFavorite({ payload }, { call, put }) {
      try {
        yield call(updateFavorite, payload);
        yield put({ type: 'querySetFavorite', payload });

        yield put({ type: 'markFavoritesChanged' });
      } catch (e) {}
    },

    *initProcess({ payload }, { put }) {
      yield put({ type: 'queryInit', payload });
    },

    *startProcess({ payload }, { call, put }) {
      try {
        const { processId, formId, ...params } = payload;
        const res = yield call(api.create, {
          route: `processEngine/start/${processId}/${formId}`,
          ...params,
        });

        if (res.processInstance) {
          SocketService.getInstance().send('joinGroup', {
            type: 'process-instance',
            groupId: _.toUpper(res.processInstance.id),
          });
        }

        yield put({ type: 'queryStart', payload: res });
      } catch (e) {
        // Error recovery: clear validation errors and reset submission state
        yield put({ type: 'queryStartError', payload: e });

        if (e?.validationErrors) {
          yield put({
            type: 'global/validationErrors',
            payload: e.validationErrors,
          });
        }
      }
    },

    *fetchGroup({ payload }, { call, put }) {
      try {
        const res = yield call(api.getById, {
          route: 'processGroup/getInfo',
          params: { select: 'id,name' },
          ...payload,
        });
        yield put({ type: 'queryGroup', payload: res });
      } catch (e) {
        history.goBack();
      }
    },

    *cleanupGroup({ payload }, { put }) {
      yield put({ type: 'queryGroup', payload });
    },

    *setActiveTab({ payload }, { put }) {
      yield put({ type: 'updateTab', payload });
    },

    *addNewProcessGroup({ payload }, { put }) {
      yield put({ type: 'newProcessGroup', payload });
    },

    *processGroupDetail({ payload }, { put }) {
      yield put({ type: 'showGroupDetail', payload });
    },

    *search({ payload }, { put }) {
      yield put({ type: 'querySearch', payload });
    },

    *paginate({ payload }, { put }) {
      yield put({ type: 'queryPage', payload });
    },

    *hideAllForms(o, { put }) {
      yield put({ type: 'hideForms' });
    },

    *reset(o, { put }) {
      yield put({ type: 'resetState' });
    },

    *cleanup({ payload }, { put }) {
      yield put({ type: 'global/validationErrors' });
      yield put({ type: 'queryClean', payload });
    },

    *onTabChange({ payload }, { put, select }) {
      if (payload === 'favorites') {
        const { favoritesChanged } = yield select((state) => state.processes);
        if (favoritesChanged) {
          yield put({ type: 'fetchFavorites', payload: { page: 1 } });
          yield put({ type: 'clearFavoritesChanged' });
        }
      }
    },
  },
  reducers: {
    queryGroups(state, { payload }) {
      return {
        ...state,
        groups: payload.items || [],
        favorite: false,
        pagination: {
          ...meta,
          ...toPaginate({ ...state.pagination, ...payload }),
        },
      };
    },

    queryList(state, { payload }) {
      return {
        ...state,
        favorite: false,
        processList: payload.items || [],
        data: payload.items || [],
        processListPagination: {
          ...state.processListPagination,
          ...toPaginate({ ...state.processListPagination, ...payload }),
        },
      };
    },

    queryFavorites(state, { payload }) {
      return {
        ...state,
        favorite: false,
        favorites: payload,
      };
    },

    queryInit(state, { payload }) {
      return {
        ...state,
        redirectTo: `/processes/start?ProcessId=${payload}`,
      };
    },

    queryConfig(state, { payload }) {
      return {
        ...state,
        activeTab: 'groups',
        processConfig: !!payload && {
          ...payload,
          behaviorCases: formFieldBehaviorsMapping(payload),
        },
      };
    },

    queryStart(state, { payload }) {
      const { command, redirectRoute, processInstance, autofire } = payload;
      if (autofire && command.command !== CoreEnums.RequestDetailCommandType.Details) {
        //  set temporally variable to persist the auto command execution
        sessionStorage.setItem('x-autoFireCommand', JSON.stringify(command));
      }

      setTimeout(() => {
        // flag in the auto open from the start process
        sessionStorage.setItem('x-autoOpenEditOperation', 1);
      }, [500]);

      return {
        ...state,
        processInstanceId: processInstance?.id,
        redirectToRoute: redirectRoute,
      };
    },

    queryStartError(state, { payload }) {
      // Error recovery: maintain form state for retry
      return {
        ...state,
        validationErrors: payload?.validationErrors || [],
      };
    },

    newProcessGroup(state, { payload }) {
      return {
        ...state,
        newGroup: payload.state,
        selectedItem: payload.item,
      };
    },

    showGroupDetail(state, { payload }) {
      return { ...state, showGroupDetail: payload };
    },

    queryGroup(state, { payload }) {
      return {
        ...state,
        currentItem: payload,
      };
    },

    updateTab(state, { payload }) {
      return {
        ...state,
        activeTab: payload,
      };
    },

    querySearch(state, { payload }) {
      return {
        ...state,
        ...(_.isString(payload) && { searchCriteria: payload }),
        ...(_.isObject(payload) && {
          [`search${_.capitalize(payload.viewContext)}Criteria`]: payload.criteria,
        }),
      };
    },

    queryPage(state, { payload }) {
      const { data, searchCriteria, searchData } = state;
      const totalCount = searchCriteria ? searchData.length : data.length;
      return {
        ...state,
        pagination: {
          ...state.pagination,
          ...toPaginate({ ...payload, totalCount }),
        },
      };
    },

    querySetFavorite(state, { payload }) {
      return {
        ...state,
        data: state.data.map((item) =>
          item.id === payload.id ? { ...item, isFavorite: !payload.isFavorite } : item,
        ),
        favorites: payload.isFavorite
          ? state.favorites.filter((item) => item.id !== payload.id)
          : state.favorites,
      };
    },

    markFavoritesChanged(state) {
      return {
        ...state,
        favoritesChanged: true,
      };
    },

    clearFavoritesChanged(state) {
      return {
        ...state,
        favoritesChanged: false,
      };
    },

    hideForms(state) {
      return {
        ...state,
        newGroup: false,
        showGroupDetail: false,
      };
    },

    queryClean(state, { payload }) {
      switch (payload) {
        case 'redirect':
          return {
            ...state,
            processList: [],
            processListPagination: meta,
            redirectTo: null,
            searchCriteria: '',
          };
        case 'start-process':
          return {
            ...state,
            validationErrors: [],
            processConfig: null,
            redirectTo: null,
            processInstanceId: null,
            redirectToRoute: null,
          };
        default:
          return initialState;
      }
    },

    resetState() {
      return initialState;
    },
  },
};

export default Model;
